import React from 'react';
import { LinkPropsType, toSimpleLink } from '../../../lib/helpers/index';
import {
	InvoiceAcceptedByPlannerEventPropsEventFragment,
	EventCategory,
	UserType,
} from '#types';
import { getPaymentInstallmentNames } from '../helpers';
import { gql } from '@apollo/client';
import rpcShared from '@rockpapercoin/rpc-shared';
import { Typography } from '@mui/material';
import { FlattenedUser } from '../../../types/user';
import { CalendarLinksProps, CalendarSourceType } from '../../CalendarLinks';

export const invoiceAcceptedByPlannerEventPropsEventFragment = gql`
	fragment invoiceAcceptedByPlannerEventPropsEvent on Event {
		id
		category
		invoice {
			id
			title
			eventDate
			contact {
				id
				firstName
				lastName
				assignedMember {
					id
				}
				customer {
					id
					email
					clientUser {
						id
						assignedPlanner {
							id
							firstName
							lastName
							user {
								id
							}
							organization {
								id
							}
						}
					}
				}
				vendor {
					id
					name
					users {
						id
						role
						user {
							id
						}
					}
				}
			}
			organization {
				id
			}
		}
		paymentInstallments {
			id
			name
		}
	}
`;

type SimpleLinkProps = {
	text: string;
	link: LinkPropsType;
};

export const getInstallmentPaymentRequestedByEventProps = (
	eventProps: {
		event: InvoiceAcceptedByPlannerEventPropsEventFragment;
		message: React.JSX.Element;
		links: {
			type: string;
			text: string;
			link: any;
			onClick?: ( () => any ) | null;
		}[];
		icon: string;
		className: string;
		calendarLinksProps?: CalendarLinksProps;
	},
	user: FlattenedUser
) => {
	if ( !eventProps.event.invoice ) {
		throw new Error( `Event ID ${ eventProps.event.id } missing required data` );
	}

	const { invoice } = eventProps.event;

	if ( !invoice.contact || !eventProps.event.paymentInstallments ) {
		throw new Error( `Event ID ${ eventProps.event.id } missing required data` );
	}

	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;

	const invoiceLinkProps: SimpleLinkProps = {
		text: invoice.title,
		link: {
			href: `/invoice/${ invoice.id }`,
		},
	};
	const invoiceLink = toSimpleLink( invoiceLinkProps );
	const customerLinkProps: SimpleLinkProps = {
		text: rpcShared.contacts.getCustomerName( invoice.contact ).fullName,
		link: {
			href: `/client/${ invoice.contact.customer.id }`,
		},
	};
	const plannerName = invoice?.contact?.customer.clientUser?.assignedPlanner
		? `${ invoice.contact.customer.clientUser.assignedPlanner.firstName } ${ invoice.contact.customer.clientUser.assignedPlanner.lastName }`
		: undefined;
	const viewerIsCustomer = user?.isLoggedIn
		? rpcShared.invoices.isCustomer( invoice, user.user.id )
		: false;

	const installmentNames = getPaymentInstallmentNames(
		eventProps.event.paymentInstallments
	);

	const viewerIsInInvoiceOrganization = orgUser
		? orgUser.organization.id === invoice.organization?.id
		: false;
	const canSendCalendarInvite =
		!!orgUser &&
		viewerIsInInvoiceOrganization &&
		( rpcShared.organizations.isAdminOrManager( orgUser.organization, orgUser ) ||
			rpcShared.invoices.isAssignedMember( invoice, orgUser ) );

	const calendarLinksProps: CalendarLinksProps | undefined = invoice.eventDate
		? {
			sourceType: CalendarSourceType.INVOICE,
			sourceId: invoice.id,
			iconOnly: false,
			canSendCalendarInvite,
		  }
		: undefined;

	const defaultEventProps = {
		icon: 'dollar',
		links: [
			{
				type: 'secondary',
				text: 'View',
				link: invoiceLinkProps.link,
			},
		],
		calendarLinksProps,
	};

	if (
		eventProps.event.category ===
		EventCategory.InstallmentPaymentRequestedByPlanner
	) {
		return {
			...defaultEventProps,
			message: (
				<Typography>
					{ plannerName } has paid { installmentNames } on { invoiceLink }.
				</Typography>
			),
		};
	} else {
		const customer = viewerIsCustomer ? (
			'You have'
		) : (
			<>
				{ viewerIsInInvoiceOrganization
					? toSimpleLink( customerLinkProps )
					: customerLinkProps.text }{ ' ' }
				has
			</>
		);
		return {
			...defaultEventProps,
			message: (
				<Typography>
					{ customer } paid { installmentNames } on { invoiceLink }.
				</Typography>
			),
		};
	}
};
